import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Tone from 'tone';

const ChambreEcouteAnhydran = () => {
  // États pour les données météo et contrôles
  const [weatherData, setWeatherData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioInitialized, setAudioInitialized] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [realTimeMode, setRealTimeMode] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [location, setLocation] = useState({ city: 'Paris', country: 'FR', lat: null, lon: null });
  
  // État pour la popup d'autorisation
  const [showAudioPopup, setShowAudioPopup] = useState(true);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  
  // Structure hiérarchique des données selon votre classification
  const [weatherControls, setWeatherControls] = useState({
    // DONNÉES MAJEURES - Impact sonore fondamental
    major: {
      temperature: 15,        // -30 à 50°C
      feelsLike: 15,         // -30 à 50°C  
      humidity: 50,          // 0-100%
      precipitation: 0,      // 0-50mm
      snow: 0               // 0-50cm
    },
    
    // DONNÉES IMPORTANTES - Couleur et texture sonore
    important: {
      windSpeed: 5,         // 0-40 m/s
      rain: 0,              // 0-50mm/h
      cloudCover: 30,       // 0-100%
      uvIndex: 3            // 0-12 (corrigé pour être plus doux)
    },
    
    // DONNÉES ANECDOTIQUES - Effets spéciaux ponctuels  
    anecdotal: {
      hour: new Date().getHours(),     // 0-23h
      pressure: 1013,                  // 950-1050 hPa
      cloudType: 'scattered',          // clear, scattered, broken, overcast
      visibility: 10,                  // 0-20km
      altitude: 100                    // 0-3000m (simulé)
    },
    
    // DONNÉES SUPERFLUES - Modulation subtile (optionnelles)
    superfluous: {
      moonPhase: 0.5,      // 0-1 (nouvelle à pleine)
      sunrise: 7,          // 0-23h
      sunset: 19          // 0-23h
    }
  });

  // Refs pour les synthétiseurs organisés par priorité
  const synthsRef = useRef({});
  const effectsRef = useRef({});
  const masterRef = useRef(null);
  const intervalRef = useRef(null);
  const harmonicsPlayingRef = useRef(false);

  // Configuration des couleurs par catégorie
  const categoryColors = {
    major: {
      temperature: '#E94B3C',
      feelsLike: '#FF6B6B', 
      humidity: '#4A90E2',
      precipitation: '#2196F3',
      snow: '#E3F2FD'
    },
    important: {
      windSpeed: '#7ED321',
      rain: '#2196F3',
      cloudCover: '#B0BEC5',
      uvIndex: '#FFB74D' // Plus doux que l'orange vif
    },
    anecdotal: {
      hour: '#9C27B0',
      pressure: '#9013FE',
      cloudType: '#607D8B',
      visibility: '#FFC107',
      altitude: '#795548'
    },
    superfluous: {
      moonPhase: '#3F51B5',
      sunrise: '#FF9800',
      sunset: '#E91E63'
    }
  };

  // Popup d'autorisation audio mise à jour
  const AudioPermissionPopup = ({ onAccept }) => (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(15, 44, 76, 0.95)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)'
    }}>
      <div style={{
        width: '90%',
        maxWidth: '800px',
        padding: '3rem',
        backgroundColor: 'rgba(15, 44, 76, 0.98)',
        borderRadius: '20px',
        border: '1px solid rgba(74, 144, 226, 0.3)',
        color: 'white',
        textAlign: 'center',
        fontFamily: '"Roboto", sans-serif',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 100,
          letterSpacing: '0.3rem',
          marginBottom: '1rem',
          color: 'rgba(126, 211, 33, 0.9)',
          textTransform: 'lowercase'
        }}>
          an·hydra·n
        </h1>
        
        <h2 style={{
          fontSize: '1.8rem',
          fontWeight: 300,
          letterSpacing: '0.15rem',
          marginBottom: '2rem',
          color: 'rgba(255,255,255,0.8)'
        }}>
          chambre d'écoute web
        </h2>
        
        <div style={{
          backgroundColor: 'rgba(74, 144, 226, 0.1)',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '2rem',
          border: '1px solid rgba(74, 144, 226, 0.2)'
        }}>
          <p style={{
            fontSize: '1.2rem',
            lineHeight: '1.6',
            marginBottom: '1.5rem',
            color: 'rgba(255,255,255,0.9)'
          }}>
            Cette installation sonore génère une <strong>composition en temps réel</strong> à partir de données météorologiques hiérarchisées.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            lineHeight: '1.4'
          }}>
            <div style={{
              background: 'rgba(233, 75, 60, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              border: '1px solid rgba(233, 75, 60, 0.3)'
            }}>
              <strong style={{ color: '#E94B3C' }}>Données majeures</strong><br/>
              🌡️ Température & ressenti<br/>
              🌊 Humidité<br/>
              🌧️ Précipitations<br/>
              ❄️ Neige
            </div>
            
            <div style={{
              background: 'rgba(74, 144, 226, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              border: '1px solid rgba(74, 144, 226, 0.3)'
            }}>
              <strong style={{ color: '#4A90E2' }}>Données importantes</strong><br/>
              💨 Vitesse du vent<br/>
              ☁️ Couverture nuageuse<br/>
              ☀️ Index UV<br/>
              🌦️ Intensité pluie
            </div>
            
            <div style={{
              background: 'rgba(126, 211, 33, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              border: '1px solid rgba(126, 211, 33, 0.3)'
            }}>
              <strong style={{ color: '#7ED321' }}>Effets spéciaux</strong><br/>
              🕐 Heure du jour<br/>
              📊 Pression atmosphérique<br/>
              👁️ Visibilité<br/>
              ⛰️ Altitude
            </div>
            
            <div style={{
              background: 'rgba(156, 39, 176, 0.1)',
              padding: '1rem',
              borderRadius: '10px',
              border: '1px solid rgba(156, 39, 176, 0.3)'
            }}>
              <strong style={{ color: '#9C27B0' }}>Modulation subtile</strong><br/>
              🌙 Phase lunaire<br/>
              🌅 Lever du soleil<br/>
              🌇 Coucher du soleil
            </div>
          </div>
          
          <div style={{
            backgroundColor: 'rgba(233, 75, 60, 0.1)',
            borderRadius: '10px',
            padding: '1rem',
            border: '1px solid rgba(233, 75, 60, 0.3)'
          }}>
            <p style={{
              fontSize: '0.9rem',
              color: 'rgba(255,255,255,0.6)',
              margin: 0
            }}>
              ⚡ <strong>Géolocalisation :</strong> Renseignez votre ville ou activez la géolocalisation pour des données temps réel.
              <br/>🎛️ Mode manuel disponible pour l'expérimentation artistique.
            </p>
          </div>
        </div>
        
        <button
          onClick={onAccept}
          style={{
            background: 'linear-gradient(135deg, rgba(126, 211, 33, 0.8), rgba(46, 128, 145, 0.8))',
            border: 'none',
            borderRadius: '30px',
            padding: '18px 40px',
            fontSize: '1.3rem',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            fontFamily: '"Roboto", sans-serif',
            letterSpacing: '0.1rem',
            textTransform: 'uppercase',
            fontWeight: 500,
            boxShadow: '0 8px 25px rgba(126, 211, 33, 0.3)'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.05) translateY(-2px)';
            e.target.style.boxShadow = '0 15px 35px rgba(126, 211, 33, 0.4)';
            e.target.style.background = 'linear-gradient(135deg, rgba(126, 211, 33, 1), rgba(46, 128, 145, 1))';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1) translateY(0)';
            e.target.style.boxShadow = '0 8px 25px rgba(126, 211, 33, 0.3)';
            e.target.style.background = 'linear-gradient(135deg, rgba(126, 211, 33, 0.8), rgba(46, 128, 145, 0.8))';
          }}
        >
          🎵 Activer l'expérience sonore
        </button>
      </div>
    </div>
  );

  // Gestion de la fermeture de popup et initialisation audio
  const handleAcceptAudio = useCallback(async () => {
    setUserHasInteracted(true);
    setShowAudioPopup(false);
    
    setTimeout(async () => {
      await initializeAudio();
      setTimeout(() => {
        if (audioInitialized) {
          togglePlayback();
        }
      }, 1000);
    }, 300);
  }, []);

  // Initialisation de Tone.js avec architecture hiérarchique
  const initializeAudio = useCallback(async () => {
    try {
      setAudioError(null);
      
      if (Tone.context.state !== 'running') {
        console.log('🎵 Démarrage du contexte audio...');
        await Tone.start();
        console.log('✅ Contexte audio démarré');
      }

      // Nettoyer les anciens synthés
      Object.values(synthsRef.current).forEach(layer => {
        if (layer.synth && typeof layer.synth.dispose === 'function') {
          layer.synth.dispose();
        }
        if (layer.effect && typeof layer.effect.dispose === 'function') {
          layer.effect.dispose();
        }
        if (layer.gain && typeof layer.gain.dispose === 'function') {
          layer.gain.dispose();
        }
        if (layer.filter && typeof layer.filter.dispose === 'function') {
          layer.filter.dispose();
        }
      });

      synthsRef.current = {};
      effectsRef.current = {};

      // Bus master
      const masterDelay = new Tone.Delay('8n').toDestination();
      await masterDelay.ready;
      
      const masterReverb = new Tone.Reverb({
        roomSize: 0.8,
        decay: 4,
        wet: 0.3
      }).connect(masterDelay);
      await masterReverb.ready;
      
      const masterFilter = new Tone.Filter({
        frequency: 2000,
        type: 'lowpass',
        rolloff: -24
      }).connect(masterReverb);

      masterRef.current = masterFilter;

      // === DONNÉES MAJEURES - Couche sonore fondamentale ===
      
      // Température → Oscillateur principal
      const tempOsc = new Tone.Oscillator({
        frequency: 220,
        type: 'sine'
      });
      const tempGain = new Tone.Gain(0).connect(masterFilter);
      tempOsc.connect(tempGain);
      synthsRef.current.temperature = { synth: tempOsc, gain: tempGain };

      // Température ressentie → Modulation de la température principale
      const feelsLikeFilter = new Tone.Filter({
        frequency: 1000,
        type: 'bandpass',
        Q: 2
      }).connect(masterFilter);
      synthsRef.current.feelsLike = { filter: feelsLikeFilter };

      // Humidité → Nappes harmoniques douces
      const humiditySynth = new Tone.Synth({
        oscillator: { type: 'sawtooth' },
        envelope: { attack: 2, decay: 0.5, sustain: 0.8, release: 3 },
        filter: { frequency: 800, type: 'lowpass' }
      });
      const humidityGain = new Tone.Gain(0).connect(masterFilter);
      const humidityChorus = new Tone.Chorus(4, 2.5, 0.5).connect(humidityGain);
      humiditySynth.connect(humidityChorus);
      synthsRef.current.humidity = { synth: humiditySynth, gain: humidityGain, effect: humidityChorus };

      // Précipitations → Percussions organiques
      const precipitationSynth = new Tone.NoiseSynth({
        noise: { type: 'pink' },
        envelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.1 }
      });
      const precipitationGain = new Tone.Gain(0).connect(masterFilter);
      precipitationSynth.connect(precipitationGain);
      synthsRef.current.precipitation = { synth: precipitationSynth, gain: precipitationGain };

      // Neige → Textures cristallines hautes fréquences
      const snowSynth = new Tone.MetalSynth({
        frequency: 2000,
        envelope: { attack: 0.001, decay: 0.5, release: 0.1 },
        harmonicity: 12,
        modulationIndex: 25,
        resonance: 3000,
        octaves: 2
      });
      const snowGain = new Tone.Gain(0).connect(masterFilter);
      snowSynth.connect(snowGain);
      synthsRef.current.snow = { synth: snowSynth, gain: snowGain };

      // === DONNÉES IMPORTANTES - Couleur sonore ===

      // Vent → Bruit modulé
      const windNoise = new Tone.Noise('pink');
      const windGain = new Tone.Gain(0).connect(masterFilter);
      const windFilter = new Tone.AutoFilter({
        frequency: '4n',
        depth: 0.8,
        baseFrequency: 200
      }).connect(windGain);
      windNoise.connect(windFilter);
      synthsRef.current.windSpeed = { synth: windNoise, gain: windGain, filter: windFilter };

      // Couverture nuageuse → Réverbération
      const cloudReverb = new Tone.Reverb({
        roomSize: 0.5,
        decay: 2,
        wet: 0
      }).connect(masterFilter);
      await cloudReverb.ready;
      effectsRef.current.cloudCover = cloudReverb;

      // UV Index → Oscillateur aigu DOUX (corrigé)
      const uvOsc = new Tone.Oscillator({
        frequency: 880, // Réduit de 1760 à 880Hz (plus agréable)
        type: 'sine'    // Changé de triangle à sine (plus doux)
      });
      const uvGain = new Tone.Gain(0).connect(masterFilter);
      const uvFilter = new Tone.Filter({
        frequency: 1200,
        type: 'lowpass', // Filtre passe-bas pour adoucir
        rolloff: -12
      }).connect(uvGain);
      uvOsc.connect(uvFilter);
      synthsRef.current.uvIndex = { synth: uvOsc, gain: uvGain, filter: uvFilter };

      // === DONNÉES ANECDOTIQUES - Effets spéciaux ===

      // Pression → Basses profondes
      const pressureSynth = new Tone.FMSynth({
        harmonicity: 0.5,
        modulationIndex: 2,
        oscillator: { type: 'sine' },
        envelope: { attack: 0.1, decay: 0.2, sustain: 0.8, release: 1 }
      });
      const pressureGain = new Tone.Gain(0).connect(masterFilter);
      pressureSynth.connect(pressureGain);
      synthsRef.current.pressure = { synth: pressureSynth, gain: pressureGain };

      // Heure → Modulation globale (filtre principal)
      const hourFilter = new Tone.Filter({
        frequency: 2000,
        type: 'lowpass',
        rolloff: -12
      }).connect(masterFilter);
      synthsRef.current.hour = { filter: hourFilter };

      // === DONNÉES SUPERFLUES - Modulation subtile ===

      // Phase lunaire → LFO sur le master reverb
      const moonLFO = new Tone.LFO({
        frequency: '32n',
        min: 0.1,
        max: 0.4
      });
      moonLFO.connect(masterReverb.wet);
      synthsRef.current.moonPhase = { lfo: moonLFO };

      setAudioInitialized(true);
      console.log('🎵 Système audio An·hydra·n initialisé avec architecture hiérarchique');
      
    } catch (error) {
      console.error('❌ Erreur initialisation audio:', error);
      setAudioError(error.message);
    }
  }, []);

  // Fonction de mapping audio hiérarchique
  const updateAudioFromWeather = useCallback((data) => {
    if (!audioInitialized || !synthsRef.current) return;

    const values = realTimeMode ? {
      // Extraction des vraies données météo
      temperature: data?.main?.temp || 15,
      feelsLike: data?.main?.feels_like || 15,
      humidity: data?.main?.humidity || 50,
      precipitation: (data?.rain?.['1h'] || 0) + (data?.snow?.['1h'] || 0),
      snow: data?.snow?.['1h'] || 0,
      windSpeed: data?.wind?.speed || 5,
      cloudCover: data?.clouds?.all || 30,
      uvIndex: data?.uvi || 3,
      pressure: data?.main?.pressure || 1013,
      visibility: (data?.visibility || 10000) / 1000,
      hour: new Date().getHours(),
      moonPhase: 0.5 // Simulé pour l'instant
    } : {
      ...weatherControls.major,
      ...weatherControls.important,
      ...weatherControls.anecdotal,
      ...weatherControls.superfluous
    };

    try {
      // === DONNÉES MAJEURES - Impact immédiat ===
      
      // Température principale
      if (synthsRef.current.temperature?.synth && synthsRef.current.temperature?.gain) {
        const tempNormalized = Math.max(0, Math.min(1, (values.temperature + 30) / 80));
        const frequency = 110 + (tempNormalized * 440);
        
        if (synthsRef.current.temperature.synth.frequency?.rampTo) {
          synthsRef.current.temperature.synth.frequency.rampTo(frequency, 1);
        }
        if (synthsRef.current.temperature.gain.gain?.rampTo) {
          synthsRef.current.temperature.gain.gain.rampTo(tempNormalized * 0.4, 1);
        }
      }

      // Humidité → Harmoniques
      if (synthsRef.current.humidity?.synth && synthsRef.current.humidity?.gain) {
        const humidityLevel = values.humidity / 100;
        
        if (synthsRef.current.humidity.gain.gain?.rampTo) {
          synthsRef.current.humidity.gain.gain.rampTo(humidityLevel * 0.3, 1);
        }
        
        // Notes harmoniques douces
        if (humidityLevel > 0.3 && !harmonicsPlayingRef.current) {
          harmonicsPlayingRef.current = true;
          
          const harmonicNotes = ['C4', 'E4', 'G4', 'C5'];
          const noteIndex = Math.floor(humidityLevel * 3.99);
          const note = harmonicNotes[noteIndex];
          
          if (synthsRef.current.humidity.synth.triggerAttackRelease) {
            synthsRef.current.humidity.synth.triggerAttackRelease(note, '2n');
          }
          
          setTimeout(() => {
            harmonicsPlayingRef.current = false;
          }, 4000);
        }
      }

      // === DONNÉES IMPORTANTES - Texture ===

      // Vent
      if (synthsRef.current.windSpeed?.gain && synthsRef.current.windSpeed?.filter) {
        const windLevel = Math.min(values.windSpeed / 40, 1);
        
        if (synthsRef.current.windSpeed.gain.gain?.rampTo) {
          synthsRef.current.windSpeed.gain.gain.rampTo(windLevel * 0.4, 1);
        }
        if (synthsRef.current.windSpeed.filter.frequency?.rampTo) {
          synthsRef.current.windSpeed.filter.frequency.rampTo(200 + (windLevel * 600), 1);
        }
      }

      // UV Index CORRIGÉ - Plus doux
      if (synthsRef.current.uvIndex?.synth && synthsRef.current.uvIndex?.gain) {
        const uvLevel = Math.min(values.uvIndex / 12, 1);
        const uvFreq = 440 + (uvLevel * 440); // 440-880Hz au lieu de 1760-2640Hz
        
        if (synthsRef.current.uvIndex.synth.frequency?.rampTo) {
          synthsRef.current.uvIndex.synth.frequency.rampTo(uvFreq, 2);
        }
        if (synthsRef.current.uvIndex.gain.gain?.rampTo) {
          synthsRef.current.uvIndex.gain.gain.rampTo(uvLevel * 0.1, 2); // Volume réduit
        }
      }

      // === DONNÉES ANECDOTIQUES - Effets spéciaux ===

      // Pression atmosphérique
      if (synthsRef.current.pressure?.gain && synthsRef.current.pressure?.synth) {
        const pressureNormalized = Math.max(0, Math.min(1, (values.pressure - 950) / 100));
        
        if (synthsRef.current.pressure.gain.gain?.rampTo) {
          synthsRef.current.pressure.gain.gain.rampTo(pressureNormalized * 0.15, 2);
        }
        
        // Déclenchement rare de basses
        if (Math.random() < 0.02) {
          const bassFreq = 40 + (pressureNormalized * 60);
          if (synthsRef.current.pressure.synth.triggerAttackRelease) {
            synthsRef.current.pressure.synth.triggerAttackRelease(bassFreq, '4n');
          }
        }
      }

      // Heure → Filtrage global
      if (synthsRef.current.hour?.filter) {
        const hourNormalized = values.hour / 24;
        const brightness = Math.sin(hourNormalized * Math.PI); // Plus brillant à midi
        const cutoffFreq = 1000 + (brightness * 2000);
        
        if (synthsRef.current.hour.filter.frequency?.rampTo) {
          synthsRef.current.hour.filter.frequency.rampTo(cutoffFreq, 5);
        }
      }

      // === DONNÉES SUPERFLUES - Modulation subtile ===

      // Phase lunaire
      if (synthsRef.current.moonPhase?.lfo) {
        const moonFreq = 0.1 + (values.moonPhase * 0.2);
        if (synthsRef.current.moonPhase.lfo.frequency?.rampTo) {
          synthsRef.current.moonPhase.lfo.frequency.rampTo(moonFreq, 10);
        }
      }

    } catch (error) {
      console.error('Erreur lors de la mise à jour audio:', error);
    }
  }, [audioInitialized, realTimeMode, weatherControls]);

  // Fonctions de contrôle play/pause
  const togglePlayback = useCallback(async () => {
    if (!userHasInteracted) {
      setShowAudioPopup(true);
      return;
    }

    try {
      if (!audioInitialized) {
        await initializeAudio();
        setTimeout(() => {
          // Démarrer les oscillateurs continus
          if (synthsRef.current.temperature?.synth?.start) {
            synthsRef.current.temperature.synth.start();
          }
          if (synthsRef.current.windSpeed?.synth?.start) {
            synthsRef.current.windSpeed.synth.start();
          }
          if (synthsRef.current.uvIndex?.synth?.start) {
            synthsRef.current.uvIndex.synth.start();
          }
          if (synthsRef.current.moonPhase?.lfo?.start) {
            synthsRef.current.moonPhase.lfo.start();
          }
          setIsPlaying(true);
          updateAudioFromWeather(weatherData);
        }, 500);
        return;
      }

      if (isPlaying) {
        // Arrêter
        Object.values(synthsRef.current).forEach(layer => {
          if (layer.synth?.stop) layer.synth.stop();
          if (layer.lfo?.stop) layer.lfo.stop();
          if (layer.gain?.gain?.rampTo) layer.gain.gain.rampTo(0, 0.5);
        });
        setIsPlaying(false);
      } else {
        // Démarrer
        if (synthsRef.current.temperature?.synth?.start) {
          synthsRef.current.temperature.synth.start();
        }
        if (synthsRef.current.windSpeed?.synth?.start) {
          synthsRef.current.windSpeed.synth.start();
        }
        if (synthsRef.current.uvIndex?.synth?.start) {
          synthsRef.current.uvIndex.synth.start();
        }
        if (synthsRef.current.moonPhase?.lfo?.start) {
          synthsRef.current.moonPhase.lfo.start();
        }
        
        setIsPlaying(true);
        updateAudioFromWeather(weatherData);
      }
    } catch (error) {
      console.error('Erreur lors du toggle playback:', error);
      setAudioError(error.message);
    }
  }, [audioInitialized, isPlaying, userHasInteracted, initializeAudio, updateAudioFromWeather, weatherData]);

  // Récupération des données météo avec géolocalisation
  const fetchWeatherData = useCallback(async () => {
    try {
      // Pour l'instant, utiliser des données simulées enrichies
      const simulatedData = {
        main: {
          temp: 10 + (Math.random() * 20),
          feels_like: 10 + (Math.random() * 20),
          humidity: 30 + (Math.random() * 50),
          pressure: 1000 + (Math.random() * 50)
        },
        wind: { speed: Math.random() * 15 },
        clouds: { all: Math.random() * 100 },
        visibility: 5000 + (Math.random() * 10000),
        rain: Math.random() > 0.8 ? { '1h': Math.random() * 5 } : undefined,
        snow: Math.random() > 0.9 ? { '1h': Math.random() * 2 } : undefined,
        uvi: Math.random() * 11,
        coord: location.lat ? { lat: location.lat, lon: location.lon } : { lat: 48.8566, lon: 2.3522 }
      };
      
      setWeatherData(simulatedData);
      console.log(`🌤 Données météo simulées pour ${location.city}:`, simulatedData);
      return simulatedData;
    } catch (error) {
      console.error('Erreur récupération météo:', error);
      return null;
    }
  }, [location]);

  // Gestion de la géolocalisation
  const handleLocationUpdate = useCallback(async (newLocation) => {
    setLocation(newLocation);
    if (realTimeMode) {
      await fetchWeatherData();
    }
  }, [realTimeMode, fetchWeatherData]);

  // Mise à jour manuelle des contrôles hiérarchiques
  const handleWeatherControlChange = useCallback((category, param, value) => {
    setWeatherControls(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [param]: value
      }
    }));
  }, []);

  // Interface de localisation
  const LocationControls = () => (
    <div style={{
      background: 'rgba(255,255,255,0.05)',
      borderRadius: '15px',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      border: '1px solid rgba(255,255,255,0.2)'
    }}>
      <h4 style={{
        color: 'white',
        fontSize: '1.1rem',
        marginBottom: '1rem',
        textAlign: 'center',
        fontWeight: 400
      }}>
        📍 Localisation pour données météo temps réel
      </h4>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        alignItems: 'center'
      }}>
        <div>
          <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
            Ville
          </label>
          <input
            type="text"
            value={location.city}
            onChange={(e) => handleLocationUpdate({...location, city: e.target.value})}
            placeholder="Paris"
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem'
            }}
          />
        </div>
        
        <div>
          <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>
            Pays (code)
          </label>
          <input
            type="text"
            value={location.country}
            onChange={(e) => handleLocationUpdate({...location, country: e.target.value})}
            placeholder="FR"
            maxLength="2"
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              color: 'white',
              fontSize: '1rem'
            }}
          />
        </div>
        
        <button
          onClick={() => {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  handleLocationUpdate({
                    ...location,
                    lat: position.coords.latitude,
                    lon: position.coords.longitude,
                    city: 'Position actuelle'
                  });
                },
                (error) => {
                  console.error('Erreur géolocalisation:', error);
                }
              );
            }
          }}
          disabled={!userHasInteracted}
          style={{
            background: 'linear-gradient(135deg, #4A90E2, #7ED321)',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            color: 'white',
            fontSize: '0.9rem',
            cursor: !userHasInteracted ? 'not-allowed' : 'pointer',
            opacity: !userHasInteracted ? 0.5 : 1,
            marginTop: '1.5rem'
          }}
        >
          📍 Géolocaliser
        </button>
      </div>
      
      <div style={{
        textAlign: 'center',
        fontSize: '0.8rem',
        color: 'rgba(255,255,255,0.6)',
        marginTop: '1rem'
      }}>
        {location.lat && location.lon 
          ? `Coordonnées: ${location.lat.toFixed(4)}, ${location.lon.toFixed(4)}`
          : 'Utilisez votre position ou entrez une ville'
        }
      </div>
    </div>
  );

  // Rendu des contrôles par catégorie
  const renderCategoryControls = (category, categoryData, title, description, accentColor) => (
    <div style={{
      marginBottom: '2rem',
      padding: '1.5rem',
      background: `rgba(${accentColor}, 0.1)`,
      borderRadius: '15px',
      border: `1px solid rgba(${accentColor}, 0.3)`
    }}>
      <h4 style={{
        color: `rgba(${accentColor}, 0.9)`,
        fontSize: '1.1rem',
        marginBottom: '0.5rem',
        textAlign: 'center',
        fontWeight: 500,
        letterSpacing: '0.1rem'
      }}>
        {title}
      </h4>
      
      <p style={{
        color: 'rgba(255,255,255,0.6)',
        fontSize: '0.85rem',
        textAlign: 'center',
        marginBottom: '1.5rem',
        fontStyle: 'italic'
      }}>
        {description}
      </p>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '1rem'
      }}>
        {Object.entries(categoryData).map(([key, value]) => {
          const configs = {
            // Données majeures
            temperature: { min: -30, max: 50, unit: '°C', label: 'Température', desc: 'Fréquence principale' },
            feelsLike: { min: -30, max: 50, unit: '°C', label: 'Ressenti', desc: 'Modulation thermique' },
            humidity: { min: 0, max: 100, unit: '%', label: 'Humidité', desc: 'Nappes harmoniques' },
            precipitation: { min: 0, max: 50, unit: 'mm', label: 'Précipitations', desc: 'Percussions organiques' },
            snow: { min: 0, max: 50, unit: 'cm', label: 'Neige', desc: 'Cristaux aigus' },
            
            // Données importantes
            windSpeed: { min: 0, max: 40, unit: 'm/s', label: 'Vent', desc: 'Textures de mouvement' },
            rain: { min: 0, max: 50, unit: 'mm/h', label: 'Intensité pluie', desc: 'Rythmes liquides' },
            cloudCover: { min: 0, max: 100, unit: '%', label: 'Nuages', desc: 'Réverbération' },
            uvIndex: { min: 0, max: 12, unit: '', label: 'UV', desc: 'Brillance douce' },
            
            // Données anecdotiques
            hour: { min: 0, max: 23, unit: 'h', label: 'Heure', desc: 'Filtrage global' },
            pressure: { min: 950, max: 1050, unit: 'hPa', label: 'Pression', desc: 'Basses profondes' },
            cloudType: { min: 0, max: 3, unit: '', label: 'Type nuages', desc: 'Effets spéciaux' },
            visibility: { min: 0, max: 20, unit: 'km', label: 'Visibilité', desc: 'Clarté sonore' },
            altitude: { min: 0, max: 3000, unit: 'm', label: 'Altitude', desc: 'Espace vertical' },
            
            // Données superflues
            moonPhase: { min: 0, max: 1, unit: '', label: 'Phase lunaire', desc: 'LFO subtil' },
            sunrise: { min: 0, max: 23, unit: 'h', label: 'Lever soleil', desc: 'Modulation douce' },
            sunset: { min: 0, max: 23, unit: 'h', label: 'Coucher soleil', desc: 'Modulation douce' }
          };
          
          const config = configs[key];
          if (!config) return null;
          
          const color = categoryColors[category][key];

          return (
            <div key={key} style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '10px',
              padding: '1rem',
              border: `1px solid ${color}30`,
              position: 'relative'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.5rem'
              }}>
                <label style={{
                  color: color,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  letterSpacing: '0.05rem'
                }}>
                  {config.label}
                </label>
                <span style={{
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 600
                }}>
                  {realTimeMode ? 
                    (weatherData ? 
                      (key === 'temperature' ? weatherData.main?.temp?.toFixed(1) :
                       key === 'feelsLike' ? weatherData.main?.feels_like?.toFixed(1) :
                       key === 'humidity' ? weatherData.main?.humidity :
                       key === 'precipitation' ? ((weatherData.rain?.['1h'] || 0) + (weatherData.snow?.['1h'] || 0)).toFixed(1) :
                       key === 'snow' ? (weatherData.snow?.['1h'] || 0).toFixed(1) :
                       key === 'windSpeed' ? weatherData.wind?.speed?.toFixed(1) :
                       key === 'cloudCover' ? weatherData.clouds?.all :
                       key === 'uvIndex' ? (weatherData.uvi || 0).toFixed(1) :
                       key === 'pressure' ? weatherData.main?.pressure :
                       key === 'visibility' ? (weatherData.visibility / 1000).toFixed(1) :
                       key === 'hour' ? new Date().getHours() :
                       value) : '--'
                    ) : 
                    value
                  }{config.unit}
                </span>
              </div>
              
              <div style={{
                fontSize: '0.7rem',
                color: 'rgba(255,255,255,0.6)',
                marginBottom: '0.5rem',
                fontStyle: 'italic'
              }}>
                {config.desc}
              </div>
              
              {!realTimeMode && userHasInteracted && (
                <input
                  type="range"
                  min={config.min}
                  max={config.max}
                  step={config.min < 1 ? 0.1 : 1}
                  value={value}
                  onChange={(e) => handleWeatherControlChange(category, key, parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: '6px',
                    borderRadius: '3px',
                    background: `linear-gradient(90deg, ${color}20 0%, ${color} ${((value - config.min) / (config.max - config.min)) * 100}%, rgba(255,255,255,0.1) ${((value - config.min) / (config.max - config.min)) * 100}%)`,
                    outline: 'none',
                    WebkitAppearance: 'none',
                    cursor: 'pointer'
                  }}
                />
              )}
              
              {realTimeMode && (
                <div style={{
                  width: '100%',
                  height: '6px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    background: `linear-gradient(90deg, ${color}60, ${color})`,
                    width: weatherData ? 
                      `${((
                        key === 'temperature' ? (weatherData.main?.temp + 30) / 80 :
                        key === 'feelsLike' ? (weatherData.main?.feels_like + 30) / 80 :
                        key === 'humidity' ? weatherData.main?.humidity / 100 :
                        key === 'precipitation' ? Math.min(((weatherData.rain?.['1h'] || 0) + (weatherData.snow?.['1h'] || 0)) / 50, 1) :
                        key === 'snow' ? Math.min((weatherData.snow?.['1h'] || 0) / 50, 1) :
                        key === 'windSpeed' ? Math.min(weatherData.wind?.speed / 40, 1) :
                        key === 'cloudCover' ? weatherData.clouds?.all / 100 :
                        key === 'uvIndex' ? Math.min((weatherData.uvi || 0) / 12, 1) :
                        key === 'pressure' ? (weatherData.main?.pressure - 950) / 100 :
                        key === 'visibility' ? Math.min((weatherData.visibility / 1000) / 20, 1) :
                        key === 'hour' ? new Date().getHours() / 24 :
                        0.5
                      ) * 100)}%` : '0%',
                    transition: 'width 1s ease-out',
                    borderRadius: '3px'
                  }} />
                </div>
              )}
              
              {!userHasInteracted && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.7)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.8rem'
                }}>
                  Activez l'audio
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Effects
  useEffect(() => {
    fetchWeatherData();
  }, [fetchWeatherData]);

  useEffect(() => {
    if (isPlaying && audioInitialized) {
      updateAudioFromWeather(weatherData);
    }
  }, [weatherControls, weatherData, isPlaying, updateAudioFromWeather, audioInitialized]);

  useEffect(() => {
    if (realTimeMode && isPlaying) {
      intervalRef.current = setInterval(() => {
        fetchWeatherData();
      }, 30000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [realTimeMode, isPlaying, fetchWeatherData]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      Object.values(synthsRef.current).forEach(layer => {
        if (layer.synth && typeof layer.synth.dispose === 'function') {
          layer.synth.dispose();
        }
        if (layer.effect && typeof layer.effect.dispose === 'function') {
          layer.effect.dispose();
        }
        if (layer.gain && typeof layer.gain.dispose === 'function') {
          layer.gain.dispose();
        }
        if (layer.filter && typeof layer.filter.dispose === 'function') {
          layer.filter.dispose();
        }
        if (layer.lfo && typeof layer.lfo.dispose === 'function') {
          layer.lfo.dispose();
        }
      });
      
      if (masterRef.current && typeof masterRef.current.dispose === 'function') {
        masterRef.current.dispose();
      }
    };
  }, []);

  return (
    <>
      {/* Popup d'autorisation audio */}
      {showAudioPopup && !userHasInteracted && (
        <AudioPermissionPopup onAccept={handleAcceptAudio} />
      )}

      {/* Interface principale */}
      <div style={{
        width: '100%',
        height: '100vh',
        background: 'linear-gradient(135deg, #0F2C4C 0%, #2E8B7A 50%, #4A90E2 100%)',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: '"Roboto", sans-serif'
      }}>
        {/* Titre */}
        <div style={{
          position: 'absolute',
          top: '2rem',
          left: '2rem',
          zIndex: 10
        }}>
          <h1 style={{
            fontSize: '3rem',
            color: 'rgba(255,255,255,0.9)',
            fontWeight: 100,
            letterSpacing: '0.2rem',
            margin: 0,
            textTransform: 'lowercase'
          }}>
            an·hydra·n
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255,255,255,0.7)',
            fontWeight: 300,
            margin: '0.5rem 0 0 0',
            letterSpacing: '0.1rem'
          }}>
            chambre d'écoute web
          </p>
        </div>

        {/* Indicateur d'état */}
        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '50%',
          transform: 'translateX(50%)',
          background: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '0.9rem',
          zIndex: 1000,
          textAlign: 'center'
        }}>
          {!userHasInteracted ? (
            <span style={{ color: '#ffd43b' }}>👆 Cliquez pour commencer l'expérience</span>
          ) : audioError ? (
            <span style={{ color: '#ff6b6b' }}>❌ Erreur: {audioError}</span>
          ) : audioInitialized ? (
            <span style={{ color: '#51cf66' }}>🎵 Audio prêt • {isPlaying ? 'En lecture' : 'En pause'} • Architecture hiérarchique</span>
          ) : (
            <span style={{ color: '#ffd43b' }}>⏳ Initialisation audio...</span>
          )}
        </div>

        {/* Contrôles principaux */}
        <div style={{
          position: 'absolute',
          top: '2rem',
          right: '2rem',
          display: 'flex',
          gap: '1rem',
          zIndex: 10
        }}>
          <button
            onClick={togglePlayback}
            disabled={!userHasInteracted || audioError !== null}
            style={{
              background: isPlaying 
                ? 'linear-gradient(135deg, #E94B3C, #FF6B6B)' 
                : 'linear-gradient(135deg, #4A90E2, #7ED321)',
              border: 'none',
              borderRadius: '25px',
              padding: '12px 24px',
              color: 'white',
              fontSize: '1rem',
              cursor: (!userHasInteracted || audioError) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              fontWeight: 500,
              letterSpacing: '0.05rem',
              opacity: (!userHasInteracted || audioError) ? 0.5 : 1
            }}
            onMouseEnter={(e) => {
              if (userHasInteracted && !audioError) {
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 8px 25px rgba(255,255,255,0.2)';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            {isPlaying ? '⏸ Pause' : '▶ Écouter'}
          </button>

          <button
            onClick={() => setRealTimeMode(!realTimeMode)}
            disabled={!userHasInteracted}
            style={{
              background: realTimeMode 
                ? 'linear-gradient(135deg, #9013FE, #673AB7)' 
                : 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '25px',
              padding: '12px 24px',
              color: 'white',
              fontSize: '1rem',
              cursor: !userHasInteracted ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              fontWeight: 500,
              letterSpacing: '0.05rem',
              opacity: !userHasInteracted ? 0.5 : 1
            }}
          >
            {realTimeMode ? '🌍 Temps réel' : '🎛 Manuel'}
          </button>

          <button
            onClick={() => setShowControls(!showControls)}
            disabled={!userHasInteracted}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '25px',
              padding: '12px 24px',
              color: 'white',
              fontSize: '1rem',
              cursor: !userHasInteracted ? 'not-allowed' : 'pointer',
              opacity: !userHasInteracted ? 0.5 : 1
            }}
          >
            {showControls ? '👁 Masquer' : '🎛 Contrôles'}
          </button>
        </div>

        {/* Panneau de contrôles hiérarchiques */}
        <div style={{
          position: 'absolute',
          bottom: showControls ? '2rem' : '-80%',
          left: '2rem',
          right: '2rem',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2rem',
          transition: 'bottom 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 10,
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h3 style={{
            color: 'white',
            fontSize: '1.5rem',
            marginBottom: '1.5rem',
            textAlign: 'center',
            fontWeight: 300
          }}>
            {realTimeMode ? 'Données météorologiques temps réel • Architecture hiérarchique' : 'Contrôles manuels • Structure hiérarchique des données météo'}
          </h3>

          {/* Interface de localisation */}
          {realTimeMode && <LocationControls />}

          {/* Données majeures */}
          {renderCategoryControls(
            'major', 
            weatherControls.major, 
            '🌡️ DONNÉES MAJEURES • Impact sonore fondamental',
            'Température, humidité, précipitations : les éléments qui définissent le paysage sonore de base',
            '233, 75, 60'
          )}

          {/* Données importantes */}
          {renderCategoryControls(
            'important', 
            weatherControls.important, 
            '🎨 DONNÉES IMPORTANTES • Couleur et texture sonore',
            'Vent, nuages, UV : les éléments qui ajoutent la couleur et le mouvement à la composition',
            '74, 144, 226'
          )}

          {/* Données anecdotiques */}
          {renderCategoryControls(
            'anecdotal', 
            weatherControls.anecdotal, 
            '✨ DONNÉES ANECDOTIQUES • Effets spéciaux ponctuels',
            'Heure, pression, visibilité : les éléments qui créent des événements sonores particuliers',
            '126, 211, 33'
          )}

          {/* Données superflues */}
          {renderCategoryControls(
            'superfluous', 
            weatherControls.superfluous, 
            '🌙 DONNÉES SUPERFLUES • Modulation subtile',
            'Phase lunaire, lever/coucher : modulations douces et atmosphériques en arrière-plan',
            '156, 39, 176'
          )}

          {/* Informations sur le système */}
          <div style={{
            marginTop: '1.5rem',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '0.9rem'
          }}>
            {!userHasInteracted ? (
              <span>🔇 Cliquez sur "Activer l'expérience sonore" pour commencer</span>
            ) : audioInitialized ? (
              <span>🎵 Système audio initialisé • {isPlaying ? 'En lecture' : 'En pause'} • 
                {Object.keys(weatherControls.major).length + Object.keys(weatherControls.important).length + Object.keys(weatherControls.anecdotal).length + Object.keys(weatherControls.superfluous).length} paramètres météo</span>
            ) : (
              <span>🔇 Cliquez sur "Écouter" pour initialiser l'audio</span>
            )}
            {realTimeMode && weatherData && userHasInteracted && (
              <span style={{ marginLeft: '1rem' }}>
                🌍 {location.city} • Mise à jour: {new Date().toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>

        {/* Message de bienvenue pour l'écoute pure */}
        {userHasInteracted && !showControls && (
          <div style={{
            position: 'absolute',
            bottom: '50%',
            left: '50%',
            transform: 'translate(-50%, 50%)',
            textAlign: 'center',
            color: 'rgba(255,255,255,0.6)',
            fontSize: '1.2rem',
            letterSpacing: '0.1rem',
            animation: 'breathe 4s ease-in-out infinite'
          }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🌊</div>
            <div>Composition météorologique en cours...</div>
            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', opacity: 0.7 }}>
              Architecture hiérarchique • {location.city} • Données {realTimeMode ? 'temps réel' : 'simulées'}
            </div>
          </div>
        )}

        {/* Visualisation ambiante */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: `
            radial-gradient(circle at 20% 80%, rgba(74, 144, 226, ${isPlaying ? 0.4 : 0.2}) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(126, 211, 33, ${isPlaying ? 0.4 : 0.2}) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(233, 75, 60, ${isPlaying ? 0.3 : 0.1}) 0%, transparent 50%)
          `,
          animation: 'float 20s ease-in-out infinite',
          zIndex: 1,
          transition: 'all 2s ease'
        }}>
          <style>
            {`
              @keyframes float {
                0%, 100% { 
                  transform: translateY(0px) rotate(0deg) scale(1); 
                  filter: hue-rotate(0deg);
                }
                25% { 
                  transform: translateY(-20px) rotate(1deg) scale(1.02); 
                  filter: hue-rotate(90deg);
                }
                50% { 
                  transform: translateY(-10px) rotate(-1deg) scale(0.98); 
                  filter: hue-rotate(180deg);
                }
                75% { 
                  transform: translateY(-15px) rotate(0.5deg) scale(1.01); 
                  filter: hue-rotate(270deg);
                }
              }
              
              @keyframes breathe {
                0%, 100% { 
                  opacity: 0.6; 
                  transform: translate(-50%, 50%) scale(1);
                }
                50% { 
                  opacity: 0.8; 
                  transform: translate(-50%, 50%) scale(1.05);
                }
              }
            `}
          </style>
        </div>
      </div>
    </>
  );
};

export default ChambreEcouteAnhydran;